import React, {useEffect} from 'react';
import {z} from 'zod';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {getGetMeQueryKey, useUpdateMe, type UpdateMeBody} from 'api/generated';
import {toast} from 'react-toastify';
import {Button, Info, InputText} from 'components';
import {cacheUpdateItem} from 'utils';
import {useMe} from 'hooks';

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  handle: z.string().min(1, 'Handle is required'),
});

interface IFormSettingsProps {}

const FormSettings = ({}: IFormSettingsProps) => {
  const me = useMe();
  const updateMe = useUpdateMe();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: {isDirty, errors},
  } = useForm({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: UpdateMeBody) => {
    const {data: res} = await updateMe.mutateAsync({data});
    if (res.data) {
      toast.success('Profile updated');
      cacheUpdateItem({
        queryKey: getGetMeQueryKey(),
        data: res.data,
      });
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  };

  useEffect(() => {
    if (me) {
      reset(me);
    }
  }, [me]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-2 max-w-xl">
      <InputText label="Email" readOnly value={me?.email} />
      <div className="grid grid-cols-2 gap-3">
        <InputText label="First Name" {...register('firstName')} error={errors.firstName?.message} />
        <InputText label="Last Name" {...register('lastName')} error={errors.lastName?.message} />
      </div>
      <div className="relative flex flex-col gap-3">
        <InputText label="Handle" {...register('handle')} error={errors.handle?.message} />
        <div className="absolute left-14 top-0.5">
          <Info
            content={
              <p className="text-slate-800 dark:text-white text-sm">
                The handle is used in project grouping for the url, e.g{' '}
                <span className="whitespace-nowrap text-primary">{`app.das-ar.com/ar/${watch('handle')}/{project}`}</span>
              </p>
            }
          />
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <div>
          <Button type="submit" text="Save" fullWidth size="small" disabled={!isDirty} loading={updateMe.isPending} />
        </div>
      </div>
    </form>
  );
};

export default React.memo(FormSettings);
